import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { axiosSalary, axiosMaster } from "../api/axiosInstance";
import { FaSearch, FaEdit, FaTrash, FaPlus, FaFileExcel } from "react-icons/fa";
import AddEditEmployeeProfile from "./AddEditEmployeeProfile";

const EmployeeProfile = () => {

  /* ================= SAFE USER ================= */

  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {}

  const groupId = storedUser?.groupId;

  /* ================= STATE ================= */

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  /* ================= FETCH ================= */

  const fetchProfiles = async (restId = 0) => {
    if (!groupId) return;

    try {
      setLoading(true);

      const res = await axiosSalary.get(
        `/EmployeeProfile/GetByGroup?groupId=${groupId}&restId=${restId}`
      );

      setData(res.data || []);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const res = await axiosMaster.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );

      const options = [
        { value: 0, label: "Select Restaurants" },
        ...(res.data || []).map((r) => ({
          value: r.restId,
          label: r.restaurantName,
        })),
      ];

      setRestaurantOptions(options);

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProfiles();
    fetchRestaurants();
  }, [groupId]);

  /* ================= FILTER ================= */

  const filteredData = useMemo(() => {

    const term = search.toLowerCase().trim();

    return [...data]
      .filter((item) => {

        if (!term) return true;

        return (
          item.employeeName?.toLowerCase().includes(term) ||
          item.empId?.toString().includes(term)
        );
      })
      .sort((a, b) => b.empId - a.empId);

  }, [data, search]);

  /* ================= ACTIONS ================= */

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this profile?")) return;

    try {
      await axiosSalary.delete(`/EmployeeProfile/${row.id}`);
      fetchProfiles(selectedRestaurant?.value || 0);
    } catch (err) {
      alert("Delete failed");
    }
  };

  /* ================= RESTAURANT FILTER ================= */

  const handleRestaurantChange = (selected) => {
    setSelectedRestaurant(selected);
    fetchProfiles(selected?.value || 0);
  };

  /* ================= EXCEL ================= */

  const exportExcel = () => {

    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      EmpId: r.empId,
      Name: `"${r.employeeName}"`,
      Shift: r.shiftId,
      Working: r.expectedWorkingMinutes,
      Salary: r.salary?.basic || 0,
      Status: r.isActive ? "Active" : "Inactive",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "EmployeeProfile.csv";
    link.click();
  };

  /* ================= SHIFT NAME ================= */

  const getShiftName = (type) => {
    switch (type) {
      case 1: return "Opening";
      case 2: return "Reporting";
      case 3: return "Closing";
      default: return "Unknown";
    }
  };

  /* ================= UI ================= */

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 border">

      {/* HEADER */}
      <div className="flex flex-col lg:flex-row justify-between gap-3 mb-4">

        <h2 className="text-xl font-bold">Employee Profile</h2>

        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">

          {/* REST SELECT */}
          <div className="w-full sm:w-48">
            <Select
              options={restaurantOptions}
              value={selectedRestaurant}
              onChange={handleRestaurantChange}
              placeholder="Restaurant..."
              isClearable
            />
          </div>

          {/* SEARCH */}
          <div className="relative w-full sm:w-48">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg text-sm w-full"
            />
          </div>

          {/* EXCEL */}
          <button
            onClick={exportExcel}
            className="bg-green-600 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2"
          >
            <FaFileExcel /> Excel
          </button>

          {/* ADD */}
          <button
            onClick={openAdd}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2"
          >
            <FaPlus /> Add
          </button>

        </div>
      </div>

      {/* TABLE */}
      <div className="hidden md:block border rounded-lg overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto">

          <table className="w-full text-sm">

            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="p-2 border">Emp Id</th>
                <th className="p-2 border">Name</th>
                <th className="p-2 border">Shift</th>
                <th className="p-2 border">Working</th>
                <th className="p-2 border">Salary</th>
                <th className="p-2 border">Status</th>
                <th className="p-2 border">Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="p-2 border">{row.empId}</td>
                  <td className="p-2 border">{row.employeeName}</td>
                  <td className="p-2 border">{getShiftName(row.shiftId)}</td>
                  <td className="p-2 border">{row.expectedWorkingMinutes}</td>
                  <td className="p-2 border text-green-600">
                    ₹ {row.salary?.basic}
                  </td>
                  <td className="p-2 border">
                    {row.isActive ? "Active" : "Inactive"}
                  </td>
                  <td className="p-2 border text-center">
  <div className="flex justify-center items-center gap-3">

    <button
      onClick={() => openEdit(row)}
      className="text-blue-600 hover:text-blue-800 transition"
    >
      <FaEdit />
    </button>

    <button
      onClick={() => handleDelete(row)}
      className="text-red-600 hover:text-red-800 transition"
    >
      <FaTrash />
    </button>

  </div>
</td>
                </tr>
              ))}
            </tbody>

          </table>

        </div>
      </div>

      {/* MOBILE */}
     <div className="md:hidden space-y-4 mt-3">

  {filteredData.map((row) => {

    const s = row.salary || {};

    return (
      <div key={row.id} className="bg-white border rounded-xl p-4 shadow-sm">

        {/* HEADER */}
        <div className="flex justify-between items-start">

          <div>
            <h3 className="font-semibold text-slate-800 text-base">
              {row.employeeName}
            </h3>

            <p className="text-xs text-gray-500">
              Emp ID: {row.empId}
            </p>
          </div>

          <span
            className={`text-xs px-2 py-1 rounded ${
              row.isActive
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {row.isActive ? "Active" : "Inactive"}
          </span>
        </div>

        {/* BASIC INFO */}
        <div className="grid grid-cols-2 gap-2 mt-3 text-sm">

          <p><b>Shift:</b> {getShiftName(row.shiftId)}</p>
          <p><b>Work:</b> {row.expectedWorkingMinutes} min</p>

          <p><b>Overtime:</b> {row.isOvertimeAllowed ? "Yes" : "No"}</p>
          <p><b>PF:</b> {s.isPFApplicable ? "Yes" : "No"}</p>

          <p><b>ESIC:</b> {s.isESICApplicable ? "Yes" : "No"}</p>
          <p><b>HRA:</b> {s.houseRentAllowance ? "Yes" : "No"}</p>

        </div>

        {/* SALARY SECTION */}
        <div className="mt-3 border-t pt-3 text-sm">

          <p className="font-semibold text-slate-700 mb-1">Salary Details</p>

          <div className="grid grid-cols-2 gap-2">

            <p>Basic: ₹ {s.basic || 0}</p>
            <p>Advance: ₹ {s.advanceDeduction || 0}</p>

            <p>Loan EMI: ₹ {s.loanEMI || 0}</p>
            <p>Fine: ₹ {s.fineApplied || 0}</p>

          </div>

        </div>

        {/* LEAVE INFO */}
        <div className="mt-3 border-t pt-3 text-sm">

          <p className="font-semibold text-slate-700 mb-1">Leave Info</p>

          <div className="grid grid-cols-2 gap-2">

            <p>Paid Leave: {s.paidLeavePerMonth || 0}</p>
            <p>Comp Off: {s.compOff || 0}</p>

          </div>

        </div>

        {/* TOTAL */}
        <div className="mt-3 bg-green-50 p-2 rounded text-sm font-semibold text-green-700">
          Total Salary: ₹ {(s.basic || 0) + (s.advanceDeduction || 0) + (s.loanEMI || 0)}
        </div>

        {/* DATES */}
        <div className="mt-3 text-xs text-gray-400">
          <p>Created: {row.entryDate}</p>
          <p>Updated: {row.lastModifyDate}</p>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex justify-end gap-3 mt-3">

          <button
            onClick={() => openEdit(row)}
            className="px-3 py-1 bg-blue-100 text-blue-600 rounded"
          >
            Edit
          </button>

          <button
            onClick={() => handleDelete(row)}
            className="px-3 py-1 bg-red-100 text-red-600 rounded"
          >
            Delete
          </button>

        </div>

      </div>
    );
  })}

</div>

      {/* MODAL */}
      {formOpen && (
        <AddEditEmployeeProfile
          editingData={editing}
          onClose={() => setFormOpen(false)}
          onSuccess={() => fetchProfiles(selectedRestaurant?.value || 0)}
        />
      )}

    </div>
  );
};

export default EmployeeProfile;